import axios from "axios";
import { toast } from "react-toastify";

const readProviderAuthFromUrl = () => {
    try {
        if (window.location.pathname !== "/provider") return null;
        const providerData = new URLSearchParams(window.location.search).get("data");
        return providerData ? JSON.parse(providerData) : null;
    } catch {
        return null;
    }
};

// Module-level references updated by updateAxiosAuth().
// Interceptors are registered once at module load so they are always
// present before the first axios request fires (RTK Query dispatches
// requests synchronously during the first render, before any useEffect
// or useLayoutEffect can run).
// Seed authRef from localStorage so that the very first requests on a
// page reload already carry the auth token (before React has had a
// chance to call updateAxiosAuth via useEffect).
const _storedAuth = (() => { try { return readProviderAuthFromUrl() || JSON.parse(localStorage.getItem('auth')); } catch { return null; } })();
const authRef = { current: _storedAuth };
const setAuthDataRef = { current: null };

const isGuestPublicRoute = () => window.location.pathname.startsWith("/guest/");

const readStoredAuth = () => {
    try {
        return JSON.parse(localStorage.getItem("auth") || "{}");
    } catch {
        return {};
    }
};

const isStaleAuthResponse = (config = {}) => {
    const requestToken = config?.params?.token || config?.params?.na_auth;
    const currentToken = readStoredAuth()?.auth || authRef.current?.auth;
    return requestToken && currentToken && requestToken !== currentToken;
};

axios.interceptors.request.use(
    (config) => {
        const authData = authRef.current;

        const tokenObj = {
            practice_id: authData?.practice?.id,
            new_chat: 1
        };

        if (authData?.is_provider) {
            tokenObj.token = authData.auth;
        } else {
            tokenObj.na_auth = authData?.auth;
        }

        const tz = new Date().getTimezoneOffset();
        config.params = { ...tokenObj, tz, ...config.params, };
        return config;
    },
    (error) => Promise.reject(error)
);

axios.interceptors.response.use(
    (response) => {
        const setAuthData = setAuthDataRef.current;
        if (isStaleAuthResponse(response?.config)) {
            return response;
        }
        if (
            (response?.data?.response?.code === -2 || response?.status === 401) &&
            window.location.pathname !== "/auth/login" &&
            !isGuestPublicRoute()
        ) {
            toast.error(response?.data?.response?.msg);
            setTimeout(() => {
                localStorage.clear();
                if (setAuthData) setAuthData({});
                window.location.href = "/auth/login";
            }, 1000);
            return;
        }
        return response;
    },
    (error) => {
        const setAuthData = setAuthDataRef.current;
        if (isStaleAuthResponse(error?.config)) {
            return Promise.reject(error);
        }
        if (
            error?.response?.status === 401 &&
            window.location.pathname !== "/auth/login" &&
            !isGuestPublicRoute()
        ) {
            localStorage.clear();
            if (setAuthData) setAuthData({});
            window.location.href = "/auth/login";
        }
        return Promise.reject(error);
    }
);

// Called once on app init and again whenever authData / setAuthData changes.
export function updateAxiosAuth(authData, setAuthData) {
    authRef.current = authData;
    setAuthDataRef.current = setAuthData;
}
